package com.havn.app.ui.theme

import androidx.compose.ui.graphics.Color

data class HavnThemeTokens(
    val type: HavnThemeType,
    val background: Color,
    val surface: Color,
    val card: Color,
    val border: Color,
    val primaryAccent: Color,
    val secondaryAccent: Color,
    val primaryText: Color,
    val secondaryText: Color,
    val mutedText: Color,
    val success: Color,
    val warning: Color,
    val danger: Color,
    val cold: Color,
    val gradientStart: Color,
    val gradientEnd: Color
) {
    val surfaceElevated: Color
        get() = card

    val accentDark: Color
        get() = primaryAccent.copy(alpha = 0.74f)
}

object HavnThemeCatalog {
    val ObsidianGold = HavnThemeTokens(
        type = HavnThemeType.OBSIDIAN_GOLD,
        background = Color(0xFF05080D),
        surface = Color(0xFF0B1118),
        card = Color(0xFF101820),
        border = Color(0xFF24313D),
        primaryAccent = Color(0xFFD6AA4F),
        secondaryAccent = Color(0xFFF2D28A),
        primaryText = Color(0xFFF7F2E8),
        secondaryText = Color(0xFFB6B0A5),
        mutedText = Color(0xFF77736B),
        success = Color(0xFF42C77B),
        warning = Color(0xFFD99A32),
        danger = Color(0xFFE85D4A),
        cold = Color(0xFF4A78D8),
        gradientStart = Color(0xFF05080D),
        gradientEnd = Color(0xFF071018)
    )

    private val MidnightSapphire = HavnThemeTokens(
        type = HavnThemeType.MIDNIGHT_SAPPHIRE,
        background = Color(0xFF030813),
        surface = Color(0xFF091425),
        card = Color(0xFF101D31),
        border = Color(0xFF263A55),
        primaryAccent = Color(0xFF4F8FD6),
        secondaryAccent = Color(0xFFBFD6F2),
        primaryText = Color(0xFFF3F7FB),
        secondaryText = Color(0xFFB7C1CE),
        mutedText = Color(0xFF758091),
        success = Color(0xFF4BCB8A),
        warning = Color(0xFFD8A84B),
        danger = Color(0xFFE86458),
        cold = Color(0xFF6BA3F2),
        gradientStart = Color(0xFF020711),
        gradientEnd = Color(0xFF0B1B32)
    )

    private val EmeraldEstate = HavnThemeTokens(
        type = HavnThemeType.EMERALD_ESTATE,
        background = Color(0xFF04100D),
        surface = Color(0xFF0B1814),
        card = Color(0xFF10221D),
        border = Color(0xFF284239),
        primaryAccent = Color(0xFFE1C170),
        secondaryAccent = Color(0xFFF4DFA1),
        primaryText = Color(0xFFF5F2E8),
        secondaryText = Color(0xFFB8B7AA),
        mutedText = Color(0xFF77796E),
        success = Color(0xFF47C987),
        warning = Color(0xFFD6A43E),
        danger = Color(0xFFE55F4F),
        cold = Color(0xFF5E8AD8),
        gradientStart = Color(0xFF03100C),
        gradientEnd = Color(0xFF0A241C)
    )

    private val PlatinumStone = HavnThemeTokens(
        type = HavnThemeType.PLATINUM_STONE,
        background = Color(0xFF0A0B0C),
        surface = Color(0xFF151719),
        card = Color(0xFF202326),
        border = Color(0xFF3B4146),
        primaryAccent = Color(0xFFD8DDE2),
        secondaryAccent = Color(0xFFFFFFFF),
        primaryText = Color(0xFFF7F7F5),
        secondaryText = Color(0xFFC5C2BA),
        mutedText = Color(0xFF85827A),
        success = Color(0xFF4ACB88),
        warning = Color(0xFFD4A64B),
        danger = Color(0xFFE46658),
        cold = Color(0xFF6D8FD4),
        gradientStart = Color(0xFF070809),
        gradientEnd = Color(0xFF1A1D20)
    )

    private val BordeauxNoir = HavnThemeTokens(
        type = HavnThemeType.BORDEAUX_NOIR,
        background = Color(0xFF0E0508),
        surface = Color(0xFF170B10),
        card = Color(0xFF221219),
        border = Color(0xFF442833),
        primaryAccent = Color(0xFFD0865D),
        secondaryAccent = Color(0xFFF0C09D),
        primaryText = Color(0xFFF8EFEA),
        secondaryText = Color(0xFFBDAFAA),
        mutedText = Color(0xFF806F6F),
        success = Color(0xFF49C97E),
        warning = Color(0xFFD49B45),
        danger = Color(0xFFE35A52),
        cold = Color(0xFF587ED5),
        gradientStart = Color(0xFF0B0306),
        gradientEnd = Color(0xFF2A0F18)
    )

    fun tokensFor(type: HavnThemeType): HavnThemeTokens = when (type) {
        HavnThemeType.OBSIDIAN_GOLD -> ObsidianGold
        HavnThemeType.MIDNIGHT_SAPPHIRE -> MidnightSapphire
        HavnThemeType.EMERALD_ESTATE -> EmeraldEstate
        HavnThemeType.PLATINUM_STONE -> PlatinumStone
        HavnThemeType.BORDEAUX_NOIR -> BordeauxNoir
    }
}
